import { EventType } from '@oneclick/analytics-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../hooks/useAnalytics';
import { EVENT_NAMES } from '../constants/eventNames';
import { CHATBOT_STATUS } from '../constants/status';
import defaultChatService from '../services/chatService';
import { transformApiMessagesToUI } from '../utils/messageTransformer';
import { useChatbotContext } from './ChatbotContext';
import { buildStreamOptions } from './streamOptionsBuilder';
import { useMockData } from './useMockData';
import { useStorageSync } from './useStorageSync';
import { useStreamingHandlers } from './useStreamingHandlers';
/**
 * Custom hook for managing chat conversations and message streaming
 * @param {Object} options - Configuration options
 * @param {Object} options.chatService - Chat service instance to use (defaults to the core service)
 * @param {Boolean} options.isVisible - Whether the chat container is currently visible
 * @param {Boolean} options.enableMockData - Whether to use mock data for development (defaults to false)
 * @returns {Object} Conversation state and methods
 */
/**
 * Simple storage abstraction for messages
 */
const messageStorage = {
  STORAGE_KEY: 'tangerine_chatbot_messages',

  save: (messages) => {
    try {
      localStorage.setItem(messageStorage.STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      console.warn('Failed to save messages:', error);
    }
  },

  load: () => {
    try {
      const saved = localStorage.getItem(messageStorage.STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Failed to load messages:', error);
      return [];
    }
  },

  clear: () => {
    try {
      localStorage.removeItem(messageStorage.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear messages:', error);
    }
  },
};

function useChatConversation(options = {}) {
  // ============================================================================
  // SETUP & CONTEXT
  // ============================================================================

  // Get the chat service to use (default or provided)
  const chatService = options.chatService || defaultChatService;
  const isVisible = options.isVisible !== undefined ? options.isVisible : true;
  const enableMockData = options.enableMockData || false;

  // Analytics hook
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation(['chatbot']);

  // Get state from Context
  const {
    currentConversationId,
    setCurrentConversationId,
    conversationStatus,
    setConversationStatus,
    chatContext,
    setChatContext,
    systemPrompt,
    isDevMode,
    model,
    temperature,
    maxTokens,
    mode,
    activityInstanceId,
    activityExtraInfo,
    messageQueue,
    dequeueMessage,
    enableExternalSearch,
    isStreamingCanvas,
    setIsStreamingCanvas,
    clearStreamingCanvas,
    setCanvasContent,
    conversationData,
    setConversationData,
    clearConversationData,
    setCanvasToolExecuting,
  } = useChatbotContext();

  // Track previous mode to detect actual changes
  const previousModeRef = useRef(mode);

  // Refs for conversation management
  const streamControllerRef = useRef(null);
  const newlyCreatedConversationRef = useRef(null);
  const realMessageIdRef = useRef(null);

  // Stream session counter - increments on every conversation change to invalidate stale callbacks
  const streamSessionRef = useRef(0);

  // ============================================================================
  // INTERNAL HOOKS (Focused Responsibilities)
  // ============================================================================

  // Handle external message changes from other tabs
  const handleExternalMessagesChange = useCallback(
    (newValue) => {
      if (mode === 'study' && newValue && Array.isArray(newValue)) {
        // This will be handled by useMessageState initialization
        console.log('[useChatConversation] External messages change detected');
      }
    },
    [mode]
  );

  // Setup storage sync for messages
  const { setInternalChange: setMessagesInternalChange } = useStorageSync(messageStorage.STORAGE_KEY, handleExternalMessagesChange);

  // Create the save function with internal change marking
  const saveMessagesWithSync = useCallback(
    (messagesToSave) => {
      try {
        setMessagesInternalChange(true);
        localStorage.setItem(messageStorage.STORAGE_KEY, JSON.stringify(messagesToSave));
        setTimeout(() => setMessagesInternalChange(false), 100);
      } catch (error) {
        console.warn('Failed to save messages:', error);
      }
    },
    [setMessagesInternalChange]
  );

  // Set the save function in messageStorage
  messageStorage.save = saveMessagesWithSync;

  // 1. Load conversation from server
  const { loadConversation, isLoadingConversation, conversationLoadError } = useConversationLoader({
    conversationId: currentConversationId,
    mode,
    chatService,
    setConversationData,
    setCanvasContent,
    clearStreamingCanvas,
    clearConversationData,
  });

  // 2. Manage message state (single source of truth)
  const { messages, addMessage, clearMessages } = useMessageState({
    conversationData,
    mode,
    saveMessagesWithSync,
  });

  // 3. Persistence
  useMessagePersistence({ messages, mode, saveMessagesWithSync });

  // Streaming state (will be refactored into useMessageStreaming later)
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [activeToolEvents, setActiveToolEvents] = useState([]);

  /**
   * Cleanup all active streams
   */
  const cleanupStreamingResources = useCallback(() => {
    // Cancel any active stream
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }

    // Reset conversation status to idle to allow new messages
    setConversationStatus(CHATBOT_STATUS.IDLE);

    // Clear streaming message to prevent stale content
    setStreamingMessage(null);
  }, [setConversationStatus]);

  /**
   * Cleanup transport resources without touching status
   * Used when preparing a new stream so we don't regress status to IDLE
   */
  const cleanupTransportOnly = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
  }, []);

  // Use extracted mock data hook
  const { loadMockData, addComplexVisualizationMock } = useMockData({
    enableMockData,
    isVisible,
    setActiveToolEvents,
    setStreamingMessage,
    setConversationStatus,
    setMessages: addMessage, // Using addMessage from useMessageState
  });

  // Use extracted streaming handlers (using addMessage instead of setMessages)
  const { handleChunk, handleComplete, handleError } = useStreamingHandlers({
    conversationStatus,
    setConversationStatus,
    setStreamingMessage,
    addMessage,
    setActiveToolEvents,
    newlyCreatedConversationRef,
    cleanupStreamingResources,
    onStreamComplete: options.onStreamComplete,
    clearStreamingCanvas,
    mode,
  });

  /**
   * Create a new conversation and set it as current
   */
  const createNewConversation = useCallback(async () => {
    try {
      // Create options object for the conversation
      const conversationOptions = {
        mode: mode, // Use mode from Redux state
        trigger: 'DIRECT',
      };

      // Include activityInstanceId for practice mode conversations
      if (mode === 'practice' && activityInstanceId) {
        conversationOptions.activityInstanceId = activityInstanceId;
      }

      // Add overrides if in dev mode
      if (isDevMode) {
        // Include system prompt if it's been customized
        if (systemPrompt && systemPrompt !== 'You are a helpful assistant that can answer questions and help with tasks.') {
          conversationOptions.systemPrompt = systemPrompt;
          console.log('Using custom system prompt:', systemPrompt);
        }

        // Create LLM config with model, temperature, and maxTokens
        conversationOptions.llmConfig = {
          model: model,
          temperature: temperature,
          maxTokens: maxTokens,
        };
        console.log('Using LLM config:', conversationOptions.llmConfig);
      }

      // Add features configuration if external search is enabled
      if (enableExternalSearch) {
        conversationOptions.features = {
          enableExternalSearch: true,
        };
      }

      // Create a new conversation with options
      const conversation = await chatService.createConversation(conversationOptions);

      // Set it as the current conversation (API now returns conversationId instead of id)
      const conversationId = conversation.conversationId || conversation.id;

      // Create initial conversation object for history
      const initialConversation = {
        id: conversationId,
        title: t('New Chat'), // Placeholder until AI generates title
        updatedAt: new Date().toISOString(),
      };

      // Notify parent if callback provided
      if (options.onConversationCreated) {
        options.onConversationCreated(initialConversation);
      }

      // Mark this conversation as newly created to avoid unnecessary loading
      newlyCreatedConversationRef.current = conversationId;

      setCurrentConversationId(conversationId);

      // Track conversation started event
      try {
        await trackEvent(EventType.CONVERSATION_STARTED, {
          conversation_id: conversationId,
          mode: mode,
        });
      } catch (error) {
        console.error('Failed to track conversation started event:', error);
      }

      return conversationId;
    } catch (error) {
      // Log for diagnostics and set error state
      console.error('Error creating conversation:', error);
      setConversationStatus(CHATBOT_STATUS.ERROR);
      return null;
    }
  }, [
    chatService,
    setCurrentConversationId,
    setConversationStatus,
    isDevMode,
    systemPrompt,
    model,
    temperature,
    maxTokens,
    mode,
    activityInstanceId,
    enableExternalSearch,
    trackEvent,
    options,
  ]);

  // ============================================================================
  // CONVERSATION LOADING LOGIC
  // ============================================================================

  // Handle conversation ID changes and load persisted messages
  useEffect(() => {
    console.log('[useChatConversation] useEffect triggered', {
      enableMockData,
      currentConversationId,
      isVisible,
      mode,
      newlyCreatedRef: newlyCreatedConversationRef.current,
      conversationDataId: conversationData?.id,
    });

    // 🎭 Si está en modo mock, no procesar lógica normal de conversación
    if (enableMockData) {
      console.log('[useChatConversation] Blocked by enableMockData');
      return;
    }

    // If conversation ID is cleared (new chat), clear messages
    if (!currentConversationId) {
      console.log('[useChatConversation] Blocked by !currentConversationId - clearing conversation');
      // Clean up any active streaming before clearing messages
      cleanupStreamingResources();

      clearMessages(); // Use useMessageState clearMessages
      setStreamingMessage(null);
      setActiveToolEvents([]);
      clearStreamingCanvas();
      setCanvasContent('');
      clearConversationData();
      return;
    }

    // Skip loading if this conversation was just created
    if (newlyCreatedConversationRef.current === currentConversationId) {
      console.log('[useChatConversation] Blocked by newlyCreatedConversationRef - just created');
      // Clear the ref to allow future loads of the same conversation
      newlyCreatedConversationRef.current = null;
      return;
    }

    // Skip if conversation already loaded (prevents clearing messages during queue processing)
    if (conversationData?.id === currentConversationId) {
      console.log('[useChatConversation] Conversation already loaded via conversationData, skipping reload');
      return;
    }

    // Load conversation details when conversation ID changes
    if (isVisible) {
      console.log('[useChatConversation] Calling loadConversation with ID:', currentConversationId);
      loadConversation(currentConversationId);
    } else {
      console.log('[useChatConversation] Blocked by !isVisible');
    }
  }, [isVisible, mode, enableMockData, currentConversationId, cleanupStreamingResources, loadConversation, clearMessages]);

  // Track previous conversation ID to detect cold start vs user switch
  const previousConversationIdRef = useRef(currentConversationId);

  // Invalidate streams and reset state when conversation changes
  // This ensures any in-flight stream callbacks are ignored after conversation switch
  // BUT skip invalidation on cold start (null → valid ID) since we just created the conversation
  useEffect(() => {
    const previousId = previousConversationIdRef.current;
    previousConversationIdRef.current = currentConversationId;

    // Skip invalidation when transitioning from null (cold start)
    // We just created this conversation and are about to stream to it
    if (previousId === null && currentConversationId !== null) {
      console.log('[useChatConversation] Skipping session invalidation for cold start transition');
      return;
    }

    // Increment session to invalidate any in-flight stream callbacks
    streamSessionRef.current += 1;

    // Immediately clear streaming state to prevent stale content from showing
    setStreamingMessage(null);
    setActiveToolEvents([]);
    setConversationStatus(CHATBOT_STATUS.IDLE);
  }, [currentConversationId, setConversationStatus]);

  // Clear messages when mode ACTUALLY changes (not on every render)
  useEffect(() => {
    // Only clear if mode actually changed from a different value
    if (previousModeRef.current !== mode) {
      console.log('[useChatConversation] Mode changed from', previousModeRef.current, 'to', mode, '- clearing messages');

      // Clear all conversation data when mode changes
      clearMessages(); // Use useMessageState clearMessages
      setStreamingMessage(null);
      setActiveToolEvents([]);

      // Clear storage based on mode
      if (mode === 'practice') {
        // Always clear storage in practice mode
        messageStorage.clear();
      }

      // Update the ref to current mode
      previousModeRef.current = mode;
    }
  }, [mode, clearMessages]);

  // Remove mirroring of assistant text into canvas for teacher-tools
  // Canvas content should only be populated by explicit canvas_delta events
  useEffect(() => {
    // Intentionally left empty to disable assistant-to-canvas mirroring
  }, [mode, isStreamingCanvas, streamingMessage, setCanvasContent]);

  // Clean up stream controller when component becomes not visible
  useEffect(() => {
    // 🎭 Si está en modo mock, no limpiar automáticamente
    if (enableMockData) return;

    // Only cleanup when transitioning from visible to not visible while streaming
    if (!isVisible && (conversationStatus === CHATBOT_STATUS.STREAMING || conversationStatus === CHATBOT_STATUS.CONNECTING)) {
      // Cancel any active stream
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }

      // Reset states
      setConversationStatus(CHATBOT_STATUS.IDLE);
      setStreamingMessage(null);
    }
  }, [isVisible, conversationStatus, setConversationStatus, enableMockData]); // Watch visibility and status changes

  // Separate effect for cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up on unmount only
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }
    };
  }, []); // Empty deps - only run on mount/unmount

  /**
   * Explicitly create a new chat - clears the state and optionally updates context
   */
  const startNewChat = useCallback(() => {
    // First update context if getChatContextData is available
    if (options.getChatContextData && typeof options.getChatContextData === 'function') {
      try {
        const newContext = options.getChatContextData();
        if (newContext && typeof newContext === 'object') {
          console.log('useChatConversation: Got new context from host:', newContext);
          setChatContext(newContext);
        } else {
          console.error('useChatConversation: getChatContextData() did not return a valid object.');
        }
      } catch (error) {
        console.error('useChatConversation: Error calling getChatContextData():', error);
      }
    }

    console.log('[useChatConversation] startNewChat - clearing messages');
    // Reset the current UI state
    clearMessages(); // Use useMessageState clearMessages
    setStreamingMessage(null);
    setConversationStatus(CHATBOT_STATUS.IDLE);
    setActiveToolEvents([]);
    clearStreamingCanvas();
    setCanvasContent('');

    // Clear persisted messages in study mode
    if (mode === 'study') {
      messageStorage.clear();
    }

    // Clean up streaming resources
    cleanupStreamingResources();

    // Clear the conversation ID and newly created tracking
    setCurrentConversationId(null);
    newlyCreatedConversationRef.current = null;
  }, [setCurrentConversationId, setConversationStatus, cleanupStreamingResources, setChatContext, options, mode, clearMessages]);

  /**
   * Set up and initiate a streaming response
   */
  const initiateStream = useCallback(
    async (message, imageFile, botMessageId, globalChatContext, messageOptions = {}) => {
      // Clean up any previous transport resources first (do not change status here)
      cleanupTransportOnly();

      // Reset the real message ID ref for this new stream
      realMessageIdRef.current = null;

      // Check if we need to create a new conversation first
      // IMPORTANT: This must happen BEFORE capturing the session, because createNewConversation()
      // triggers a context update (setCurrentConversationId) which increments streamSessionRef.
      // If we capture session first, it becomes stale immediately after conversation creation.
      let conversationForMessage = currentConversationId;

      if (!conversationForMessage) {
        // In teacher-tools, conversationId must come from the URL/provider; do not auto-create
        if (mode === 'teacher-tools') {
          handleError(new Error('Missing conversation id for teacher-tools'), null, setStreamingMessage, setConversationStatus, streamControllerRef);
          clearStreamingCanvas();
          return;
        }
        try {
          conversationForMessage = await createNewConversation();
          if (!conversationForMessage) {
            throw new Error('Failed to create conversation');
          }
        } catch (error) {
          handleError(error, null, setStreamingMessage, setConversationStatus, streamControllerRef);
          clearStreamingCanvas();
          return;
        }
      }

      // Capture session AFTER conversation is confirmed to exist
      // This ensures the session matches the current state after any context updates
      const streamSession = streamSessionRef.current;

      // Helper to check if this stream is still valid (conversation hasn't changed)
      const isStale = () => streamSession !== streamSessionRef.current;

      // Handler for when backend sends the real message ID
      const handleMessageComplete = (messageId) => {
        if (isStale()) return; // Ignore if conversation changed
        realMessageIdRef.current = messageId;
      };

      // Wrapped chunk handler with staleness check
      const wrappedHandleChunk = (chunk) => {
        if (isStale()) {
          console.log('[Stream] Ignoring chunk - session invalidated');
          return;
        }
        handleChunk(chunk);
      };

      // Set up callbacks for streaming (using extracted handlers)
      const wrappedHandleComplete = (fullText) => {
        // Guard: Only process if conversation hasn't changed
        if (isStale()) {
          console.log('[Stream] Ignoring completion - session invalidated');
          // Still clean up resources even if stale
          cleanupStreamingResources();
          return;
        }
        // Pass the real message ID (if received) to handleComplete
        handleComplete(fullText, botMessageId, globalChatContext, conversationForMessage, realMessageIdRef.current);
        // Reset the ref after use
        realMessageIdRef.current = null;
        // Clear streaming canvas state after completion
        clearStreamingCanvas();
        // Clear canvas tool executing flag and remove canvas tool for teacher-tools mode
        if (mode === 'teacher-tools') {
          // Immediately remove canvas tool to prevent it transferring to the new final message
          // (activeTools is passed to last message, and the streaming message is about to be replaced)
          setActiveToolEvents((prev) => prev.filter((event) => event.toolName !== 'canvas_generation'));
          setCanvasToolExecuting(false);
        }
      };

      const wrappedHandleError = (error) => {
        // Guard: Only show error if conversation hasn't changed
        if (isStale()) {
          console.log('[Stream] Ignoring error - session invalidated');
          return;
        }
        handleError(error, currentConversationId, setStreamingMessage, setConversationStatus, streamControllerRef);
        // No retry - let the error message show to the user
        clearStreamingCanvas();
        // Clear canvas tool executing flag and mark canvas tool as failed for teacher-tools mode
        if (mode === 'teacher-tools') {
          // Mark the canvas tool as failed
          setActiveToolEvents((prev) => prev.map((event) => (event.toolName === 'canvas_generation' ? { ...event, status: 'failed' } : event)));
          setCanvasToolExecuting(false);
        }
      };

      // Initiate streaming with appropriate endpoint
      try {
        // Stream to conversation and get controller to abort if needed

        // Build stream options using extracted utility
        const streamOptions = buildStreamOptions({
          mode,
          globalChatContext,
          isDevMode,
          model,
          temperature,
          maxTokens,
          systemPrompt,
          studentResponse: messageOptions.studentResponse,
        });

        // Track if this is the first canvas delta for this stream
        let isFirstCanvasDelta = true;

        const streamController = await chatService.streamChatResponseForConversation(
          conversationForMessage,
          message,
          streamOptions,
          wrappedHandleChunk,
          wrappedHandleComplete,
          wrappedHandleError,
          imageFile,
          // Pass canvas handler only for teacher-tools mode
          mode === 'teacher-tools'
            ? (fullContent) => {
                // Guard: Ignore canvas updates if conversation changed
                if (isStale()) {
                  console.log('[Stream] Ignoring canvas delta - session invalidated');
                  return;
                }

                // On first canvas delta, set status to STREAMING and inject tool event
                if (isFirstCanvasDelta) {
                  setConversationStatus(CHATBOT_STATUS.STREAMING);

                  // Inject canvas tool event on first delta (not in sendMessage)
                  // This ensures loading indicator only shows when canvas is actually being generated
                  setActiveToolEvents((prev) => [
                    ...prev,
                    {
                      type: 'tool_invocation',
                      toolName: 'canvas_generation',
                      toolType: 'canvas_generation',
                      status: 'started',
                      timestamp: new Date().toISOString(),
                      id: `canvas-generation-${Date.now()}`,
                    },
                  ]);

                  isFirstCanvasDelta = false;
                }

                // Update both streaming and persistent canvas state
                setIsStreamingCanvas(fullContent || '');
                setCanvasContent(fullContent || '');
              }
            : null,
          // Pass handler for capturing real message ID from backend
          handleMessageComplete
        );

        // Track analytics event for message sent
        try {
          const analyticsEventPayload = {
            conversation_id: conversationForMessage,
            mode: mode,
          };

          // Add kc_id, kc_reference and activity_id for practice mode
          if (mode === 'practice') {
            // Always include these fields for practice mode, use empty string if not defined
            analyticsEventPayload.activity_id = activityInstanceId || '';
            analyticsEventPayload.kc_id = activityExtraInfo?.associatedKcId || '';
            analyticsEventPayload.kc_reference = activityExtraInfo?.associatedKcReferenceId || '';
          }

          await trackEvent(EVENT_NAMES.STUDENT_MESSAGE_SENT, analyticsEventPayload);
        } catch (analyticsError) {
          console.error('Failed to track message sent event:', analyticsError);
          // Don't throw the error to avoid breaking the chat functionality
        }

        // Save the controller for possible abort
        streamControllerRef.current = streamController;
      } catch (error) {
        wrappedHandleError(error);
      }
    },
    [
      chatService,
      conversationStatus,
      currentConversationId,
      createNewConversation,
      cleanupTransportOnly,
      setConversationStatus,
      handleChunk,
      handleComplete,
      handleError,
      systemPrompt,
      isDevMode,
      model,
      temperature,
      maxTokens,
      mode,
      setCanvasToolExecuting,
      setIsStreamingCanvas,
      setCanvasContent,
      clearStreamingCanvas,
    ]
  );

  /**
   * Handle sending a message, with streaming response
   */
  const sendMessage = useCallback(
    async (message, imageFile = null, options = {}) => {
      // Don't process messages if component isn't visible
      // Allow empty messages for teacher-tools mode to trigger initial stream
      if (!isVisible) return;
      if (!message.trim() && !imageFile && mode !== 'teacher-tools') return;

      const { studentResponse } = options;

      // Get the updated context from the parent component using getChatContextData
      // This ensures we have the most recent course ID and other context values
      let globalChatContext = chatContext;

      // If the parent provided a context getter function, use it
      if (options.getChatContextData && typeof options.getChatContextData === 'function') {
        try {
          const updatedContext = options.getChatContextData();
          if (updatedContext && typeof updatedContext === 'object') {
            globalChatContext = updatedContext;
          }
        } catch (error) {
          // Intentionally ignore context update errors
          void error;
        }
      }

      // Create message object for the UI
      const userMessage = {
        id: Date.now(),
        text: message,
        sender: 'user',
        timestamp: new Date(),
        context: globalChatContext,
        image: imageFile ? URL.createObjectURL(imageFile) : null,
        imageName: imageFile ? imageFile.name : null,
      };

      // Debug logging for teacher-tools mode
      if (mode === 'teacher-tools') {
        console.log('[sendMessage] Creating user message:', userMessage);
      }

      // Add message to UI immediately using useMessageState
      addMessage(userMessage);
      if (mode === 'teacher-tools') {
        console.log('[sendMessage] Added user message via addMessage');
      }

      // Reset streaming state
      setStreamingMessage(null);
      setConversationStatus(CHATBOT_STATUS.CONNECTING);

      // Set canvas tool executing flag (only for teacher-tools mode)
      // Note: Tool event injection moved to canvas delta handler (on first delta arrival)
      if (mode === 'teacher-tools') {
        setCanvasToolExecuting(true);
      }

      // Create a placeholder for the bot response
      const botMessageId = Date.now() + 1;
      const placeholder = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
        context: globalChatContext,
      };

      setStreamingMessage(placeholder);

      // Start the stream with image file
      await initiateStream(message, imageFile, botMessageId, globalChatContext, { studentResponse });
    },
    [chatContext, isVisible, setConversationStatus, initiateStream, options, mode, addMessage, setCanvasToolExecuting]
  );

  // Compute isStreaming state
  const isStreaming = conversationStatus === CHATBOT_STATUS.STREAMING || conversationStatus === CHATBOT_STATUS.CONNECTING;

  // 4. Process message queue
  useMessageQueue({
    isVisible,
    messageQueue,
    isStreaming,
    sendMessage,
    dequeueMessage,
    mode,
    activityInstanceId,
    currentConversationId,
    messages,
  });

  /**
   * Load previous conversations for the user
   */
  const loadConversations = useCallback(async () => {
    try {
      const response = await chatService.getAllConversations();
      return response.conversations || [];
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  }, [chatService, mode]);

  return {
    messages,
    streamingMessage,
    activeToolEvents,
    isStreaming,
    conversationStatus,
    sendMessage,
    loadConversations,
    startNewChat,
    createNewConversation,
    loadMockData,
    addComplexVisualizationMock,
    isLoadingConversation,
    conversationLoadError,
  };
}

// =============================================================================
// INTERNAL FOCUSED HOOKS
// =============================================================================

/**
 * Hook for loading conversation from API
 * Responsibility: Fetch conversation data from server
 * Does NOT modify messages state
 */
function useConversationLoader({ conversationId, mode, chatService, setConversationData, setCanvasContent, clearStreamingCanvas, clearConversationData }) {
  // Use context state for isLoadingConversation (allows external control via forceStartLoading)
  const { isLoadingConversation, setIsLoadingConversation } = useChatbotContext();
  const [conversationLoadError, setConversationLoadError] = useState(null);
  const loadedConversationIdRef = useRef(null);

  const loadConversation = useCallback(
    async (id) => {
      // Skip if already loaded
      if (loadedConversationIdRef.current === id) {
        console.log('[useConversationLoader] Already loaded:', id);
        return null;
      }

      setConversationLoadError(null);
      setIsLoadingConversation(true);

      try {
        // Fetch conversation details
        const conversationDetails = await chatService.getConversation(id, mode);

        // Store full conversation object in context
        setConversationData(conversationDetails);

        // Set canvas content for teacher-tools mode
        if (mode === 'teacher-tools' && conversationDetails.canvas) {
          setCanvasContent(conversationDetails.canvas.content || '');
        }

        // Clear streaming canvas
        clearStreamingCanvas();

        // Mark as loaded
        loadedConversationIdRef.current = id;
        console.log('[useConversationLoader] Loaded conversation:', id);

        return conversationDetails;
      } catch (error) {
        console.error('[useConversationLoader] Error loading conversation:', error);
        setConversationLoadError('An error occurred. Please try again.');
        return null;
      } finally {
        setIsLoadingConversation(false);
      }
    },
    [chatService, mode, setConversationData, setCanvasContent, clearStreamingCanvas]
  );

  // Clear loaded ref when conversation cleared
  useEffect(() => {
    if (!conversationId) {
      loadedConversationIdRef.current = null;
      clearConversationData();
    }
  }, [conversationId, clearConversationData]);

  return {
    loadConversation,
    isLoadingConversation,
    conversationLoadError,
  };
}

/**
 * Hook for managing message state (single source of truth)
 * Responsibility: Manage local messages array
 * Initialize once from server data, then append-only
 */
function useMessageState({ conversationData, mode, saveMessagesWithSync }) {
  const [messages, setMessages] = useState([]);
  const initializedConversationRef = useRef(null);

  // Initialize messages from conversationData (ONCE per conversation)
  useEffect(() => {
    if (conversationData?.id && initializedConversationRef.current !== conversationData.id) {
      console.log('[useMessageState] Initializing messages for conversation:', conversationData.id);
      const transformedMessages = transformApiMessagesToUI(conversationData.messages || []);
      setMessages(transformedMessages);
      initializedConversationRef.current = conversationData.id;

      // Update localStorage in study mode
      if (mode === 'study' && transformedMessages.length > 0) {
        saveMessagesWithSync(transformedMessages);
      }
    }
  }, [conversationData, mode, saveMessagesWithSync]);

  // Add message (append-only)
  const addMessage = useCallback((message) => {
    console.log('[useMessageState] Adding message:', message);
    setMessages((prev) => [...prev, message]);
  }, []);

  // Clear messages (explicit user action only)
  const clearMessages = useCallback(() => {
    console.log('[useMessageState] Clearing messages');
    setMessages([]);
    initializedConversationRef.current = null;
  }, []);

  return {
    messages,
    addMessage,
    clearMessages,
  };
}

/**
 * Hook for processing message queue
 * Responsibility: Process queued messages and trigger sending
 */
function useMessageQueue({ isVisible, messageQueue, isStreaming, sendMessage, dequeueMessage, mode, activityInstanceId, currentConversationId, messages }) {
  useEffect(() => {
    // Only process queue when visible, not streaming, and has messages
    if (isVisible && messageQueue.length > 0 && !isStreaming) {
      // For teacher-tools ensure we have a conversationId before sending queued messages
      if (mode === 'teacher-tools' && !currentConversationId) {
        console.log('[useMessageQueue] Blocked: No conversationId in teacher-tools mode');
        return;
      }

      const { message, imageFile, options } = messageQueue[0];

      if (mode === 'teacher-tools') {
        console.log('[useMessageQueue] Processing message:', { message, imageFile, options, messagesLength: messages.length });
      }

      // Defensive: drop duplicate empty init messages after first response
      if (mode === 'teacher-tools' && (!message || !message.trim()) && messages.length > 0) {
        console.log('[useMessageQueue] Dropping empty duplicate message');
        dequeueMessage();
        return;
      }

      // Check if the queued message belongs to the current activity (practice mode only)
      if (mode === 'practice' && options?.activityInstanceId && options.activityInstanceId !== activityInstanceId) {
        // Message is from a different activity, drop it
        console.log('[useMessageQueue] Dropping queued message from different activity:', options.activityInstanceId, 'current:', activityInstanceId);
        dequeueMessage();
        return;
      }

      if (mode === 'teacher-tools') {
        console.log('[useMessageQueue] Calling sendMessage with:', message);
      }

      // Send the message with its options
      sendMessage(message, imageFile, options);

      // Remove the message from queue
      dequeueMessage();
    }
  }, [isVisible, messageQueue, isStreaming, sendMessage, dequeueMessage, mode, activityInstanceId, currentConversationId, messages]);
}

/**
 * Hook for message persistence to localStorage
 * Responsibility: Sync messages to localStorage (study mode only)
 */
function useMessagePersistence({ messages, mode, saveMessagesWithSync }) {
  // Save messages to storage when they change (only in study mode)
  useEffect(() => {
    if (mode === 'study' && messages.length > 0) {
      saveMessagesWithSync(messages);
    }
  }, [mode, messages, saveMessagesWithSync]);
}

export default useChatConversation;
